import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Chip, MenuItem, Select,
  FormControl, InputLabel, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, TablePagination, IconButton, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import apiService from '../services/api';

interface Article {
  id: string;
  title: string;
  status: string;
  is_public: boolean;
  is_expired: boolean;
  category_id: string | null;
  category_name: string | null;
  created_by_name: string | null;
  expires_at: string | null;
  view_count: number;
  updated_at: string;
}

interface Category { id: string; name: string; }

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
};

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd/MM/yyyy HH:mm'); } catch { return iso; }
}

export default function KnowledgeBasePage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<Article[]>([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [canAuthor, setCanAuthor] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    apiService.getCurrentUser?.().then((u: any) => {
      setCanAuthor(u?.can_author_kb || u?.is_admin || false);
      setIsAdmin(u?.is_admin || false);
    }).catch(() => {});
    apiService.kbListCategories().then(setCategories).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.kbListArticles({
        status: statusFilter === 'all' ? undefined : statusFilter,
        category_id: categoryFilter || undefined,
        search: search || undefined,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      });
      setArticles(data.articles);
      setTotal(data.total);
    } catch {
      setError('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, search, page, rowsPerPage]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this article? This cannot be undone.')) return;
    try {
      await apiService.kbDeleteArticle(id);
      load();
    } catch {
      setError('Failed to delete article');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>Knowledge Base</Typography>
        {canAuthor && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/knowledge-base/new')}>
            New Article
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            size="small"
            placeholder="Search articles…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} /> }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select value={categoryFilter} label="Category" onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
              <MenuItem value="">All categories</MenuItem>
              {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1e2330' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Public</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Author</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Updated</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Expires</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 600 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={28} sx={{ my: 2 }} /></TableCell></TableRow>
            ) : articles.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No articles found</TableCell></TableRow>
            ) : articles.map((a) => (
              <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/knowledge-base/${a.id}`)}>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{a.title}</Typography>
                </TableCell>
                <TableCell>{a.category_name || <span style={{ color: '#888' }}>—</span>}</TableCell>
                <TableCell>
                  <Chip
                    label={a.is_expired ? 'expired' : a.status}
                    color={a.is_expired ? 'error' : STATUS_COLORS[a.status] ?? 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{a.is_public ? <Chip label="Public" size="small" color="info" /> : '—'}</TableCell>
                <TableCell>{a.created_by_name || '—'}</TableCell>
                <TableCell>{fmtDate(a.updated_at)}</TableCell>
                <TableCell>{a.expires_at ? fmtDate(a.expires_at) : '—'}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`/knowledge-base/${a.id}`)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                  {canAuthor && (
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/knowledge-base/${a.id}/edit`)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                  {isAdmin && (
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[50]}
        />
      </TableContainer>
    </Box>
  );
}
